import { MdOutlineHistory } from 'react-icons/md'

import { buildCrmHistoryEntries, describeHistoryValue } from '@/Containers/CrmSection/Components/History'
import { HistoryDiff, HistoryField, HistoryFields, HistoryMuted, HistoryQuote } from '@/Ship/Components/History'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { formatDateUk } from '@/Ship/Helpers/fieldViews'
import { formatMoneyWithCurrency } from '@/Ship/Helpers/formatNumber'

import type { CrmHistoryEventMap, CrmHistoryItem } from '@/Containers/CrmSection/Components/History'
import type { HistoryEntry } from '@/Ship/Components/History'
import type { ReactNode } from 'react'

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

/** Статус із `payload` (`{code, title, color}`) — тим самим бейджем, що в колонці. */
const statusTag = (value: unknown): ReactNode => {
    if (!isRecord(value)) return <HistoryMuted>—</HistoryMuted>

    const title = typeof value['title'] === 'string' ? value['title'] : describeHistoryValue(value['code'])
    const color = typeof value['color'] === 'string' ? value['color'] : undefined

    return <StatusTag label={title} color={color} inline />
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null)

/**
 * Подробиці подій заявки (`SPEC-021` §4.7, колонка `payload`): статус
 * «було → стало», причина й коментар програшу, опис проблеми й вирішення,
 * сума й дата курсу прибутку. Документи — як у клієнта (база CRM).
 */
const details = (event: string, payload: Record<string, unknown>): ReactNode | undefined => {
    switch (event) {
        case 'application_created':
            return 'status' in payload ? (
                <HistoryFields>
                    <HistoryField label="Статус">{statusTag(payload['status'])}</HistoryField>
                </HistoryFields>
            ) : null

        case 'application_status_changed':
            return (
                <HistoryFields>
                    <HistoryField label="Статус">
                        <HistoryDiff from={statusTag(payload['old'])} to={statusTag(payload['new'])} strike={false} />
                    </HistoryField>
                </HistoryFields>
            )

        case 'application_lost': {
            const comment = text(payload['comment'])

            return (
                <HistoryFields>
                    <HistoryField label="Був статус">{statusTag(payload['old'])}</HistoryField>
                    <HistoryField label="Причина">{describeHistoryValue(payload['reason'])}</HistoryField>
                    {comment ? (
                        <HistoryField label="Коментар">
                            <HistoryQuote>{comment}</HistoryQuote>
                        </HistoryField>
                    ) : null}
                </HistoryFields>
            )
        }

        case 'application_problem_reported':
        case 'application_problem_resolved': {
            const description = text(payload['description'])
            const state = event === 'application_problem_reported' ? payload['old'] : payload['new']

            return (
                <HistoryFields>
                    <HistoryField label={event === 'application_problem_reported' ? 'Був статус' : 'Новий статус'}>
                        {statusTag(state)}
                    </HistoryField>
                    {description ? (
                        <HistoryField label={event === 'application_problem_reported' ? 'Опис проблеми' : 'Як вирішено'}>
                            <HistoryQuote>{description}</HistoryQuote>
                        </HistoryField>
                    ) : null}
                </HistoryFields>
            )
        }

        case 'application_profit_calculated': {
            const amount = typeof payload['amount'] === 'number' ? payload['amount'] : Number(payload['amount'])
            const rateDate = payload['rateDate']

            return (
                <HistoryFields>
                    <HistoryField label="Прибуток">
                        {Number.isFinite(amount) ? formatMoneyWithCurrency(amount, 'UAH') : '—'}
                    </HistoryField>
                    {rateDate ? <HistoryField label="Курс НБУ на">{formatDateUk(rateDate)}</HistoryField> : null}
                </HistoryFields>
            )
        }

        default:
            return undefined
    }
}

/**
 * Події історії заявки → рядки стрічки `HistoryTimeline`. Спершу — загальний
 * будівник CRM (підпис, тон, автор, документи), поверх — подробиці власних
 * подій заявки, бо її `payload` інший, ніж у клієнта.
 */
export const buildApplicationHistoryEntries = (items: readonly CrmHistoryItem[], map: CrmHistoryEventMap): HistoryEntry[] =>
    buildCrmHistoryEntries(items, map).map((entry, index) => {
        const item = items[index]

        if (!item) return entry

        const own = details(item.event, isRecord(item.payload) ? item.payload : {})

        return own === undefined ? entry : { ...entry, details: own, icon: entry.icon ?? <MdOutlineHistory /> }
    })
